/**
 * MobileModeContext — single source of truth for "is this a real mobile device?"
 *
 * Uses user-agent detection (not viewport width) so narrow desktop browser windows
 * are NOT treated as mobile. This intentionally differs from the existing
 * useDeviceDetection() hook (viewport ≤768px), which drives only responsive layout
 * and is left completely unchanged.
 *
 * Consume with useMobileMode():
 *   const isMobile = useMobileMode();
 */

import React, { createContext, useContext, useMemo } from 'react';

// ─── Detection ────────────────────────────────────────────────────────────────

/**
 * Returns true when the user-agent string identifies a real mobile phone.
 * Tablets are excluded — they have enough screen real estate for the full app.
 * This is computed once per page load; user-agent never changes mid-session.
 */
function detectTrueMobile(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  const w = window.innerWidth;

  // Positive: common mobile UA patterns (phones, not tablets)
  const isMobileUA =
    /android(?!.*tablet)|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua);

  // Negative: tablets share some UA tokens — exclude them
  const isTabletUA = /ipad|tablet|playbook|silk/i.test(ua) || (isMobileUA && w >= 768);

  return isMobileUA && !isTabletUA;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const MobileModeContext = createContext<boolean>(false);

export const MobileModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Computed once — user-agent cannot change mid-session
  const isMobile = useMemo(() => detectTrueMobile(), []);

  return <MobileModeContext.Provider value={isMobile}>{children}</MobileModeContext.Provider>;
};

/**
 * Returns true only on a real mobile phone (user-agent based).
 * Never returns true for a narrow desktop browser window.
 */
export function useMobileMode(): boolean {
  return useContext(MobileModeContext);
}
